import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Button } from "./ui/Button";
import { Field, Input, Select } from "./ui/Input";
import type { RequestField, RequestFieldScope } from "../lib/types";

// Inline field manager rendered inside a request modal so admins
// (Owner / L1) can add, rename, retype, flag required, or remove
// fields without leaving the form. Every change is persisted
// immediately and the shared query for the scope is invalidated so
// every user's form re-renders with the new catalogue. Shared
// between the Training and Time-off request modals.
export function InlineRequestFieldManager({
  scope, fields, onChanged,
}: {
  scope: RequestFieldScope;
  fields: RequestField[];
  // Called whenever the field list changes in a way that the open
  // draft's `metadata` should follow — a rename moves a value to the
  // new key so the user's typed input isn't silently lost, a delete
  // drops the value, and an add seeds an empty value. The updater
  // receives the current metadata object and returns the next one.
  onChanged: (
    updateMetadata: (
      prev: Record<string, string | number | boolean | null>,
    ) => Record<string, string | number | boolean | null>,
  ) => void;
}) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState<{
    label: string; fieldType: RequestField["fieldType"]; required: boolean;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["requestFields", scope] });
  }

  const updateMut = useMutation({
    mutationFn: (args: { id: string; patch: Partial<RequestField>; priorKey: string }) =>
      api.updateRequestField(args.id, args.patch).then((r) => ({ r, priorKey: args.priorKey })),
    onSuccess: ({ r, priorKey }) => {
      if (r.key !== priorKey) {
        onChanged((prev) => {
          const { [priorKey]: carry, ...rest } = prev;
          return { ...rest, [r.key]: carry ?? "" };
        });
      }
      invalidate();
      setError(null);
    },
    onError: (e: { message?: string }) => setError(e?.message ?? "Failed"),
  });
  const deleteMut = useMutation({
    mutationFn: (args: { id: string; key: string }) =>
      api.deleteRequestField(args.id).then(() => args.key),
    onSuccess: (key) => {
      onChanged((prev) => {
        const { [key]: _gone, ...rest } = prev;
        return rest;
      });
      invalidate();
      setError(null);
    },
    onError: (e: { message?: string }) => setError(e?.message ?? "Failed"),
  });
  const createMut = useMutation({
    mutationFn: (d: { label: string; fieldType: RequestField["fieldType"]; required: boolean }) =>
      api.createRequestField({
        scope,
        label: d.label,
        key: keyFromLabel(d.label),
        fieldType: d.fieldType,
        required: d.required,
        sortOrder: (fields[fields.length - 1]?.sortOrder ?? 0) + 10,
      }),
    onSuccess: (row) => {
      onChanged((prev) => ({
        ...prev,
        [row.key]: row.fieldType === "checkbox" ? false : "",
      }));
      invalidate();
      setAdding(null);
      setError(null);
    },
    onError: (e: { message?: string }) => setError(e?.message ?? "Failed"),
  });

  return (
    <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded text-sm space-y-2">
      <div className="text-xs text-slate-500">
        Changes apply immediately and to every officer's request form.
      </div>
      {error && <div className="text-xs text-red-700">{error}</div>}

      <div className="space-y-1">
        {fields.map((f) => (
          <FieldRow
            key={f.id}
            field={f}
            onSave={(patch) => updateMut.mutate({ id: f.id, patch, priorKey: f.key })}
            onDelete={() => deleteMut.mutate({ id: f.id, key: f.key })}
          />
        ))}
        {fields.length === 0 && (
          <div className="text-xs text-slate-500 italic">No fields yet.</div>
        )}
      </div>

      {!adding ? (
        <button
          type="button"
          className="text-xs font-medium text-sky-700 hover:text-sky-900"
          onClick={() =>
            setAdding({ label: "", fieldType: "text", required: false })}
        >
          + Add field
        </button>
      ) : (
        <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-slate-200">
          <Field label="Label">
            <Input
              autoFocus
              value={adding.label}
              onChange={(e) => setAdding({ ...adding, label: e.target.value })}
              placeholder="e.g. Course Title"
            />
          </Field>
          <Field label="Type">
            <Select
              value={adding.fieldType}
              onChange={(e) =>
                setAdding({ ...adding, fieldType: e.target.value as RequestField["fieldType"] })}
            >
              <option value="text">Text</option>
              <option value="number">Number</option>
              <option value="checkbox">Checkbox</option>
            </Select>
          </Field>
          <label className="flex items-center gap-1 text-xs text-slate-700 mb-2">
            <input
              type="checkbox"
              className="h-3 w-3"
              checked={adding.required}
              onChange={(e) => setAdding({ ...adding, required: e.target.checked })}
            />
            Required
          </label>
          <Button
            size="sm"
            disabled={!adding.label.trim() || createMut.isPending}
            onClick={() => createMut.mutate(adding)}
          >
            {createMut.isPending ? "Adding…" : "Add"}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setAdding(null)}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}

function FieldRow({
  field, onSave, onDelete,
}: {
  field: RequestField;
  onSave: (patch: Partial<RequestField>) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(field.label);
  const [fieldType, setFieldType] = useState<RequestField["fieldType"]>(field.fieldType);
  const [required, setRequired] = useState(field.required);
  const [armedDelete, setArmedDelete] = useState(false);

  if (!editing) {
    return (
      <div className="flex items-center gap-2 py-1">
        <span className="flex-1 truncate">
          <span className="font-medium">{field.label}</span>
          <span className="ml-2 text-xs text-slate-500 capitalize">
            ({field.fieldType}{field.required ? ", required" : ""})
          </span>
        </span>
        <button
          type="button"
          className="text-xs text-sky-700 hover:text-sky-900"
          onClick={() => setEditing(true)}
        >
          Edit
        </button>
        {armedDelete ? (
          <button
            type="button"
            className="text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded px-2 py-0.5"
            onClick={() => { setArmedDelete(false); onDelete(); }}
          >
            Confirm delete
          </button>
        ) : (
          <button
            type="button"
            className="text-xs text-red-700 hover:text-red-900"
            onClick={() => setArmedDelete(true)}
          >
            Delete
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-end gap-2 py-1">
      <Field label="Label">
        <Input
          autoFocus
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
      </Field>
      <Field label="Type">
        <Select
          value={fieldType}
          onChange={(e) =>
            setFieldType(e.target.value as RequestField["fieldType"])}
        >
          <option value="text">Text</option>
          <option value="number">Number</option>
          <option value="checkbox">Checkbox</option>
        </Select>
      </Field>
      <label className="flex items-center gap-1 text-xs text-slate-700 mb-2">
        <input
          type="checkbox"
          className="h-3 w-3"
          checked={required}
          onChange={(e) => setRequired(e.target.checked)}
        />
        Required
      </label>
      <Button
        size="sm"
        disabled={!label.trim()}
        onClick={() => {
          const renamed = label.trim() !== field.label;
          onSave({
            label: label.trim(),
            fieldType,
            required,
            // Renaming the label syncs the stable `key` too so the
            // admin isn't stuck with a stale snake_case identifier.
            ...(renamed ? { key: keyFromLabel(label) } : {}),
          });
          setEditing(false);
        }}
      >
        Save
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
        Cancel
      </Button>
    </div>
  );
}

export function keyFromLabel(label: string): string {
  return label.trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// One input per request_field, driven by the field's type. The
// value shape (string for text/number, boolean for checkbox) is
// what gets stored into time_off.metadata.
export function RequestFieldInput({
  field, value, onChange,
}: {
  field: RequestField;
  value: string | number | boolean | null | undefined;
  onChange: (v: string | number | boolean) => void;
}) {
  if (field.fieldType === "checkbox") {
    return (
      <label className="flex items-center gap-2 text-sm text-slate-700 mt-4">
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
        />
        {field.label}
        {field.required && <span className="text-amber-600">*</span>}
      </label>
    );
  }
  const label = field.label + (field.required ? " *" : "");
  if (field.fieldType === "number") {
    return (
      <Field label={label}>
        <Input
          type="number"
          value={value === null || value === undefined ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      </Field>
    );
  }
  return (
    <Field label={label}>
      <Input
        value={value === null || value === undefined ? "" : String(value)}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}
